import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { calcProfit } from '../lib/calcProfit';
import { validateLoad } from '../lib/validateLoad';
import RateNegotiatorPanel from '../components/RateNegotiator/RateNegotiatorPanel';
import { generateInvoicePDF, generateSettlementPDF, generateRateConfirmationPDF } from '../pdfUtils';
import { inputStyle, bigBtn, smallBtn, formOverlay, formContainer, sectionLabel, card, fieldRow, fieldLabel, fieldVal, inlineInput, tableStyle, thStyle, trStyle, dropdown, dropdownItem, selectedBadge } from '../lib/styles';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const WORKFLOW_STAGES = [
  { key:'BOOKED',            label:'BOOKED',              color:'#aaa',     statusMap:'ACTIVE' },
  { key:'DISPATCHED',        label:'DISPATCHED',          color:'#4488FF',  statusMap:'ACTIVE' },
  { key:'EN_ROUTE_PICKUP',   label:'EN ROUTE PICKUP',     color:'#FFD700',  statusMap:'IN TRANSIT' },
  { key:'AT_PICKUP',         label:'AT PICKUP',           color:'#FF8C00',  statusMap:'IN TRANSIT' },
  { key:'LOADED',            label:'LOADED',              color:'#9B59B6',  statusMap:'IN TRANSIT' },
  { key:'EN_ROUTE_DELIVERY', label:'EN ROUTE DELIVERY',   color:'#1ABC9C',  statusMap:'IN TRANSIT' },
  { key:'AT_DELIVERY',       label:'AT DELIVERY',         color:'#3498DB',  statusMap:'IN TRANSIT' },
  { key:'DELIVERED',         label:'DELIVERED',           color:'#32CD32',  statusMap:'DELIVERED' },
];

const emptyLoad = {
  load_number:"", customer_name:"", equipment_type:"", status:"ACTIVE",
  commodity:"", weight_lbs:0, po_number:"", special_instructions:"",
  pickup_date:"", pickup_city:"", pickup_state:"",
  shipper_name:"", shipper_address:"", shipper_phone:"", shipper_email:"",
  shipper_appt_required:false, shipper_hours:"",
  delivery_date:"", delivery_city:"", delivery_state:"",
  consignee_name:"", consignee_address:"", consignee_phone:"", consignee_email:"",
  consignee_appt_required:false, consignee_hours:"",
  rate_total:0, loaded_miles:0, deadhead_miles:0,
  fuel_cost_per_mile:0, driver_pay_per_mile:0, fixed_cost_per_mile:0,
  assigned_driver_id:"",
};

interface ControlTowerProps {
  data: any[];
  selectedLoad: any;
  setSelectedLoad: (v: any) => void;
  drivers: any[];
  assets: any[];
  crmContacts: any[];
  companyId: string | null;
  newLoad: typeof emptyLoad;
  setNewLoad: (v: typeof emptyLoad) => void;
  showEntryForm: boolean;
  setShowEntryForm: (v: boolean) => void;
  fetchRoomData: () => void;
  addJakeAlert: (alert: { level:'RED'|'YELLOW'|'GREEN'; message:string; load_number?:string; driver_name?:string }) => void;
  resolveAlertsForLoad?: (loadNum: string) => void;
}

export default function ControlTower({ data, selectedLoad, setSelectedLoad, drivers, assets, crmContacts, companyId, newLoad, setNewLoad, showEntryForm, setShowEntryForm, fetchRoomData, addJakeAlert, resolveAlertsForLoad }: ControlTowerProps) {
  const [editDraft, setEditDraft] = useState<any>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  const [loadNotes, setLoadNotes] = useState<any[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [brokerSearch, setBrokerSearch] = useState("");
  const [brokerSelected, setBrokerSelected] = useState<any>(null);
  const [shipperSearch, setShipperSearch] = useState("");
  const [shipperSelected, setShipperSelected] = useState<any>(null);
  const [consigneeSearch, setConsigneeSearch] = useState("");
  const [consigneeSelected, setConsigneeSelected] = useState<any>(null);
  const [jakeSuggestion, setJakeSuggestion] = useState<{driver:any;reason:string}|null>(null);
  const [jakeSuggLoading, setJakeSuggLoading] = useState(false);

  const [validationWarnings, setValidationWarnings] = useState<{field:string;message:string}[]>([]);
  const [calcingMiles, setCalcingMiles] = useState(false);
  const [driverEta, setDriverEta] = useState<string|null>(null);

  const calcDistanceWithDuration = (origin: string, dest: string): Promise<{miles:number;secs:number}|null> => {
    return new Promise(resolve => {
      const g = (window as any).google;
      if (!g?.maps?.DistanceMatrixService || !origin.trim() || !dest.trim()) { resolve(null); return; }
      new g.maps.DistanceMatrixService().getDistanceMatrix(
        { origins:[origin], destinations:[dest], travelMode:g.maps.TravelMode.DRIVING, unitSystem:g.maps.UnitSystem.IMPERIAL },
        (resp:any, status:any) => {
          if (status !== 'OK') { resolve(null); return; }
          const el = resp?.rows?.[0]?.elements?.[0];
          if (!el?.distance?.value) { resolve(null); return; }
          resolve({ miles: Math.round(el.distance.value / 1609.34), secs: el.duration?.value || 0 });
        }
      );
    });
  };

  const calcDistanceMiles = async (origin: string, dest: string): Promise<number|null> => {
    const r = await calcDistanceWithDuration(origin, dest);
    return r ? r.miles : null;
  };

  // ── Load board filter + sort ──
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [bandFilter, setBandFilter] = useState("ALL");
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc"|"desc">("desc");
  const [statusConfirm, setStatusConfirm] = useState("");

  useEffect(() => {
    if (selectedLoad?.id) fetchLoadNotes(selectedLoad.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoad?.id]);

  const fetchLoadNotes = async (loadId: string) => {
    setNotesLoading(true);
    const { data: rows } = await supabase.from("load_notes").select("*").eq("load_id", loadId).order("created_at", { ascending: false });
    setLoadNotes(rows || []); setNotesLoading(false);
  };

  const addLoadNote = async () => {
    if (!selectedLoad?.id) return;
    const text = noteDraft.trim(); if (!text) return;
    const { data: inserted, error } = await supabase.from("load_notes").insert({ load_id: selectedLoad.id, author_type:"user", author_label:"Dispatcher", note:text }).select("*").single();
    if (error) { alert(`Note insert failed: ${error.message}`); return; }
    setLoadNotes(prev => [inserted, ...prev]); setNoteDraft("");
    // Push note to assigned driver instantly
    if (selectedLoad.assigned_driver_id) {
      supabase.functions.invoke('push-notify', { body: {
        driverId: selectedLoad.assigned_driver_id,
        title: `Dispatch — Load #${selectedLoad.load_number}`,
        body: text.slice(0, 120),
        url: '/driver',
      }}).catch(() => {});
    }
  };

  const updateLoadProfit = async (load: any) => {
    const p = calcProfit(load); if (!p) return;
    await supabase.from("loads").update({ total_cost:p.total_cost, net_profit:p.net_profit, profit_margin_percent:p.profit_margin_percent, rpm:p.rpm, is_low_hanging:p.rpm<1.8, fuel_estimated_cost:p.fuel_estimated_cost, driver_pay_estimated:p.driver_pay_estimated, operating_estimated_cost:0 }).eq("id", load.id);
  };

  const scoreAndAlertLoad = async (load: any) => {
    if (!load?.id) return;
    const p = calcProfit(load);
    const band = p.band as 'RED' | 'YELLOW' | 'GREEN';
    if (!load.profit_band) {
      await supabase.from('loads').update({ profit_band: band }).eq('id', load.id);
    }
    if (band === 'RED') {
      addJakeAlert({ level: 'RED', message: `Load #${load.load_number} scored RED — RPM $${p.rpm.toFixed(2)}, margin ${p.profit_margin_percent.toFixed(1)}%`, load_number: load.load_number });
    } else if (band === 'YELLOW') {
      addJakeAlert({ level: 'YELLOW', message: `Load #${load.load_number} scored YELLOW — RPM $${p.rpm.toFixed(2)}, margin ${p.profit_margin_percent.toFixed(1)}%`, load_number: load.load_number });
    }
  };

  const autoCreateCrmContact = async (entityName: string, entityType: string, extra: any): Promise<string|null> => {
    const dedupeKey = `${companyId}_${entityName.toLowerCase().trim().replace(/\s+/g,"_")}_${entityType}`;
    const { data: existing } = await supabase.from("crm_contacts").select("id").eq("company_id", companyId).eq("dedupe_key", dedupeKey).maybeSingle();
    if (existing) return existing.id;
    const { data: inserted, error } = await supabase.from("crm_contacts").insert({ company_id:companyId, entity_name:entityName.trim(), entity_type:entityType, status:"NEW", source:"auto-load", dedupe_key:dedupeKey, ...extra }).select("id").single();
    if (error) { console.error("CRM auto-create failed:", error.message); return null; }
    return inserted.id;
  };

  const geocodeAddress = async (address: string, city: string, state: string): Promise<{ lat: number; lng: number } | null> => {
    const query = [address, city, state].filter(Boolean).join(', ');
    if (!query.trim()) return null;
    try {
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`);
      const json = await res.json();
      const loc = json?.results?.[0]?.geometry?.location;
      if (loc) return { lat: loc.lat, lng: loc.lng };
    } catch { /* geocoding failed — coordinates will be null */ }
    return null;
  };

  const showConfirm = (msg: string) => {
    setStatusConfirm(msg);
    setTimeout(() => setStatusConfirm(""), 2500);
  };

  const updateLoadStatus = async (newStatus: string) => {
    if (!selectedLoad?.id) return;
    const updates: any = { status: newStatus };
    if (newStatus === 'IN TRANSIT') updates.dispatched_at = new Date().toISOString();
    if (newStatus === 'DELIVERED') updates.delivered_at = new Date().toISOString();
    const { data: updated, error } = await supabase.from('loads').update(updates).eq('id', selectedLoad.id).select('*').single();
    if (error) { alert(`Status update failed: ${error.message}`); return; }
    setSelectedLoad(updated); fetchRoomData();
    showConfirm(`✓ Status updated to ${newStatus}`);
    addJakeAlert({ level: newStatus === 'DELIVERED' ? 'GREEN' : 'YELLOW', message: `Load #${selectedLoad.load_number} status updated to ${newStatus}`, load_number: selectedLoad.load_number });

    // Auto-backhaul: when DELIVERED, check Supabase directly for next load (data[] hasn't refreshed yet)
    if (newStatus === 'DELIVERED' && selectedLoad.assigned_driver_id) {
      const { data: nextLoads } = await supabase.from('loads')
        .select('id')
        .eq('company_id', companyId)
        .eq('assigned_driver_id', selectedLoad.assigned_driver_id)
        .in('status', ['ACTIVE','IN TRANSIT'])
        .neq('id', selectedLoad.id)
        .limit(1);

      const hasNextLoad = (nextLoads || []).length > 0;
      const driverName = drivers.find(d => d.id === selectedLoad.assigned_driver_id)?.full_name || 'Driver';

      if (!hasNextLoad) {
        const origin = selectedLoad.delivery_city && selectedLoad.delivery_state
          ? `${selectedLoad.delivery_city}, ${selectedLoad.delivery_state}`
          : null;
        const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

        addJakeAlert({
          level: 'YELLOW',
          message: `${driverName} delivered Load #${selectedLoad.load_number}${origin ? ` in ${origin}` : ''}. No next load assigned — Jake is checking the load boards for backhaul opportunities.`,
          driver_name: driverName,
        });

        if (origin) {
          // Check if any load board credentials exist before invoking
          const { data: creds } = await supabase.from('load_board_credentials').select('id').eq('company_id', companyId).eq('is_active', true).limit(1);
          if (creds && creds.length > 0) {
            supabase.functions.invoke('jake-loadboard-hunter', {
              body: { companyId, driverId: selectedLoad.assigned_driver_id, origin, destination: null, date: tomorrow, triggeredBy: 'auto_backhaul' }
            });
          } else {
            addJakeAlert({
              level: 'YELLOW',
              message: `No load board credentials configured. Go to Settings → Load Board Credentials to add DAT, Truckstop, or Coyote login so Jake can hunt loads automatically.`,
            });
          }
        }
      }
    }
  };

  const updateWorkflowStatus = async (wfStatus: string) => {
    if (!selectedLoad?.id) return;
    const stage = WORKFLOW_STAGES.find(s => s.key === wfStatus);
    const updates: any = { workflow_status: wfStatus };
    if (stage) updates.status = stage.statusMap;
    if (wfStatus === 'DISPATCHED') updates.dispatched_at = new Date().toISOString();
    if (wfStatus === 'DELIVERED') updates.delivered_at = new Date().toISOString();
    const { data: updated, error } = await supabase.from('loads').update(updates).eq('id', selectedLoad.id).select('*').single();
    if (error) { alert(`Workflow update failed: ${error.message}`); return; }
    setSelectedLoad(updated); fetchRoomData();
    if (resolveAlertsForLoad) resolveAlertsForLoad(String(selectedLoad.load_number));
    // Auto-send 214 status update if this load came in via EDI
    if (updated.edi_source || updated.edi_sender_id) {
      supabase.functions.invoke('edi-send', { body: { loadId: selectedLoad.id, txType: '214', workflowStatus: wfStatus } }).catch(() => {});
    }
    // Auto-send rate confirmation email on dispatch
    if (wfStatus === 'DISPATCHED') {
      supabase.functions.invoke('rate-conf-email', { body: { loadId: selectedLoad.id, companyId } }).catch(() => {});
    }
    // Send push notification to driver on dispatch
    if (wfStatus === 'DISPATCHED' && updated.assigned_driver_id) {
      supabase.functions.invoke('push-notify', { body: { driverId: updated.assigned_driver_id, title: 'Load Dispatched', body: `Load #${selectedLoad.load_number} — ${updated.pickup_city} to ${updated.delivery_city}. Check your load details.`, url: '/driver' } }).catch(() => {});
    }
    showConfirm(`✓ ${stage?.label || wfStatus}`);
    const alertLevel = wfStatus === 'DELIVERED' ? 'GREEN' : 'YELLOW';
    addJakeAlert({ level: alertLevel, message: `Load #${selectedLoad.load_number} → ${stage?.label || wfStatus}`, load_number: selectedLoad.load_number });
  };

  const suggestDriverForLoad = useCallback(async (load: any) => {
    if (!companyId || !load) return;
    setJakeSuggLoading(true); setJakeSuggestion(null);
    const { data: drvs } = await supabase.from('drivers').select('*').eq('company_id', companyId).eq('status','ACTIVE');
    if (!drvs || drvs.length === 0) { setJakeSuggLoading(false); return; }
    const today = new Date();
    const scored = drvs.map((d: any) => {
      let score = 0;
      const reasons: string[] = [];
      if (d.hos_status === 'OFF DUTY') { score += 40; reasons.push('OFF DUTY → available'); }
      else if (d.hos_status === 'ON DUTY') { score += 20; reasons.push('ON DUTY'); }
      else { score -= 20; reasons.push(`Currently ${d.hos_status}`); }
      const hasActiveLoad = data.some((l:any) => l.assigned_driver_id === d.id && ['ACTIVE','IN TRANSIT'].includes((l.status||'').toUpperCase()));
      if (!hasActiveLoad) { score += 30; reasons.push('No active load'); }
      else { score -= 40; reasons.push('Already on a load'); }
      if (d.cdl_expiration) {
        const exp = new Date(d.cdl_expiration);
        const daysLeft = Math.floor((exp.getTime() - today.getTime()) / 86400000);
        if (daysLeft < 0) { score -= 100; reasons.push('CDL EXPIRED'); }
        else if (daysLeft < 30) { score -= 20; reasons.push(`CDL expires in ${daysLeft}d`); }
        else { score += 10; reasons.push('CDL valid'); }
      }
      if (d.dot_physical_expiration) {
        const exp = new Date(d.dot_physical_expiration);
        const daysLeft = Math.floor((exp.getTime() - today.getTime()) / 86400000);
        if (daysLeft < 0) { score -= 100; reasons.push('DOT Physical EXPIRED'); }
        else if (daysLeft < 30) { score -= 10; reasons.push(`DOT Physical expires in ${daysLeft}d`); }
      }
      return { driver: d, score, reason: reasons.slice(0,3).join(' | ') };
    });
    scored.sort((a:any, b:any) => b.score - a.score);
    const best = scored[0];
    if (best && best.score > -50) setJakeSuggestion({ driver: best.driver, reason: best.reason });
    setJakeSuggLoading(false);
  }, [companyId, data]);

  const saveEditDraft = async () => {
    if (!editDraft?.id) return;
    setEditSaving(true);
    const payload = { ...editDraft };
    delete payload.id; delete payload.company_id; delete payload.created_at;
    const { data: updated, error } = await (supabase as any).from('loads').update(payload).eq('id', editDraft.id).select('*').single();
    if (error) { alert(`Save failed: ${error.message}`); setEditSaving(false); return; }
    await updateLoadProfit(updated);
    setSelectedLoad(updated);
    setEditDraft({ ...updated });
    fetchRoomData();
    setEditSaving(false);
    setEditSaved(true);
    setTimeout(() => setEditSaved(false), 2500);
  };

  const openDrillDown = (item: any) => {
    setSelectedLoad(item);
    setEditDraft({ ...item });
    setEditSaved(false);
  };

  const resetLoadForm = () => {
    setNewLoad(emptyLoad);
    setBrokerSearch(""); setBrokerSelected(null);
    setShipperSearch(""); setShipperSelected(null);
    setConsigneeSearch(""); setConsigneeSelected(null);
  };

  const handleSave = async () => {
    if (!companyId) { alert("Company not loaded yet."); return; }
    if (!newLoad.load_number.trim()) { alert("Load # is required."); return; }

    // Jake completeness validation — warn but don't block
    const validation = validateLoad(newLoad as any);
    if (!validation.complete) {
      setValidationWarnings(validation.missing);
    } else {
      setValidationWarnings([]);
    }

    if ((!brokerSelected || brokerSelected.isNew) && newLoad.customer_name?.trim()) await autoCreateCrmContact(newLoad.customer_name, "BROKER", { phone:null, email:null });
    if ((!shipperSelected || shipperSelected.isNew) && newLoad.shipper_name?.trim()) await autoCreateCrmContact(newLoad.shipper_name, "SHIPPER", { phone:newLoad.shipper_phone||null, email:newLoad.shipper_email||null, address1:newLoad.shipper_address||null, city:newLoad.pickup_city||null, state:newLoad.pickup_state||null });
    if ((!consigneeSelected || consigneeSelected.isNew) && newLoad.consignee_name?.trim()) await autoCreateCrmContact(newLoad.consignee_name, "RECEIVER", { phone:newLoad.consignee_phone||null, email:newLoad.consignee_email||null, address1:newLoad.consignee_address||null, city:newLoad.delivery_city||null, state:newLoad.delivery_state||null });
    // Geocode pickup and delivery for driver app GPS geofencing
    const [pickupCoords, deliveryCoords] = await Promise.all([
      geocodeAddress(newLoad.shipper_address||'', newLoad.pickup_city||'', newLoad.pickup_state||''),
      geocodeAddress(newLoad.consignee_address||'', newLoad.delivery_city||'', newLoad.delivery_state||''),
    ]);

    const payload = { company_id:companyId, load_number:newLoad.load_number.trim(), customer_name:newLoad.customer_name?.trim()||null, equipment_type:newLoad.equipment_type||null, status:newLoad.status||"ACTIVE", commodity:newLoad.commodity?.trim()||null, weight_lbs:newLoad.weight_lbs||null, po_number:newLoad.po_number?.trim()||null, special_instructions:newLoad.special_instructions?.trim()||null, pickup_date:newLoad.pickup_date||null, pickup_city:newLoad.pickup_city?.trim()||null, pickup_state:newLoad.pickup_state?.trim()||null, pickup_lat:pickupCoords?.lat||null, pickup_lng:pickupCoords?.lng||null, shipper_name:shipperSelected?.entity_name||newLoad.shipper_name?.trim()||null, shipper_address:newLoad.shipper_address?.trim()||null, shipper_phone:shipperSelected?.phone||newLoad.shipper_phone?.trim()||null, shipper_email:shipperSelected?.email||newLoad.shipper_email?.trim()||null, shipper_appt_required:newLoad.shipper_appt_required||false, shipper_hours:newLoad.shipper_hours?.trim()||null, delivery_date:newLoad.delivery_date||null, delivery_city:newLoad.delivery_city?.trim()||null, delivery_state:newLoad.delivery_state?.trim()||null, delivery_lat:deliveryCoords?.lat||null, delivery_lng:deliveryCoords?.lng||null, consignee_name:consigneeSelected?.entity_name||newLoad.consignee_name?.trim()||null, consignee_address:newLoad.consignee_address?.trim()||null, consignee_phone:consigneeSelected?.phone||newLoad.consignee_phone?.trim()||null, consignee_email:consigneeSelected?.email||newLoad.consignee_email?.trim()||null, consignee_appt_required:newLoad.consignee_appt_required||false, consignee_hours:newLoad.consignee_hours?.trim()||null, rate_total:newLoad.rate_total||0, loaded_miles:newLoad.loaded_miles||null, deadhead_miles:newLoad.deadhead_miles||null, fuel_cost_per_mile:newLoad.fuel_cost_per_mile||null, driver_pay_per_mile:newLoad.driver_pay_per_mile||null, fixed_cost_per_mile:newLoad.fixed_cost_per_mile||null, assigned_driver_id:newLoad.assigned_driver_id||null, workflow_status:validation.complete?null:'INCOMPLETE' };
    const { data: inserted, error } = await supabase.from("loads").insert(payload).select("*").single();
    if (error) { alert(`Load insert failed: ${error.message}`); return; }
    await updateLoadProfit(inserted);
    await scoreAndAlertLoad(inserted);
    fetchRoomData(); setShowEntryForm(false); resetLoadForm(); setValidationWarnings([]);
  };

  const handleExportInvoice = async () => {
    if (!selectedLoad) return;
    const driver = drivers.find(d => d.id === selectedLoad.assigned_driver_id);
    await generateInvoicePDF({ ...selectedLoad, driver_name: driver?.full_name || '' });
  };
  const handleExportRateConf = async () => {
    if (!selectedLoad) return;
    const driver = drivers.find(d => d.id === selectedLoad.assigned_driver_id);
    await generateRateConfirmationPDF({ ...selectedLoad, driver_name: driver?.full_name || '' });
  };
  const handleExportSettlement = async () => {
    if (!selectedLoad) return;
    const driver = drivers.find(d => d.id === selectedLoad.assigned_driver_id);
    const payPercent = driver?.pay_rate_percent || driver?.pay_percent || 0;
    const driverData = { id: driver?.id || '', name: driver?.full_name || 'Driver', email: driver?.email || '', phone: driver?.phone || '', pay_percent: payPercent };
    await generateSettlementPDF({ ...selectedLoad }, driverData);
  };

  return (
    <>
      {/* ── CREATE LOAD FORM ── */}
      {showEntryForm && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"580px", maxHeight:"90vh", overflowY:"auto" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>NEW LOAD ENTRY</h3>
            <div style={sectionLabel}>LOAD INFO</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Load # *" style={inputStyle} value={newLoad.load_number} onChange={e=>setNewLoad({...newLoad,load_number:e.target.value})} />
              <input type="text" placeholder="PO / Ref #" style={inputStyle} value={newLoad.po_number} onChange={e=>setNewLoad({...newLoad,po_number:e.target.value})} />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <select style={inputStyle as any} value={newLoad.equipment_type} onChange={e=>setNewLoad({...newLoad,equipment_type:e.target.value})}>
                <option value="">Equipment Type</option>
                {["Box Truck","Cargo Van","Sprinter","Flatbed","Dry Van","Reefer","Other"].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              <select style={inputStyle as any} value={newLoad.status} onChange={e=>setNewLoad({...newLoad,status:e.target.value})}>
                {["ACTIVE","PENDING","IN TRANSIT","DELIVERED","CANCELLED"].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Commodity" style={inputStyle} value={newLoad.commodity} onChange={e=>setNewLoad({...newLoad,commodity:e.target.value})} />
              <input type="number" placeholder="Weight (lbs)" style={inputStyle} value={newLoad.weight_lbs||""} onChange={e=>setNewLoad({...newLoad,weight_lbs:Number(e.target.value)})} />
            </div>
            <div style={sectionLabel}>ASSIGN DRIVER</div>
            <select style={inputStyle as any} value={newLoad.assigned_driver_id} onChange={e=>setNewLoad({...newLoad,assigned_driver_id:e.target.value})}>
              <option value="">Select Driver (optional)...</option>
              {drivers.map(d=><option key={d.id} value={d.id}>{d.full_name} — {d.current_city||"?"}, {d.current_state||"?"} [{d.hos_status||"OFF DUTY"}]</option>)}
            </select>
            <div style={sectionLabel}>BROKER / CUSTOMER</div>
            <div style={{ position:"relative" }}>
              <input type="text" placeholder="Search or type broker name..." style={inputStyle} value={brokerSearch} onChange={e=>{setBrokerSearch(e.target.value);setBrokerSelected(null);setNewLoad({...newLoad,customer_name:e.target.value});}} />
              {brokerSearch.length > 1 && !brokerSelected && (
                <div style={dropdown}>
                  {crmContacts.filter(c=>c.entity_name.toLowerCase().includes(brokerSearch.toLowerCase())).map(c=>(
                    <div key={c.id} style={dropdownItem} onClick={()=>{setBrokerSelected(c);setBrokerSearch(c.entity_name);setNewLoad({...newLoad,customer_name:c.entity_name});}}>{c.entity_name} <span style={{color:"#777"}}>({c.entity_type})</span></div>
                  ))}
                  <div style={{...dropdownItem,color:"#32CD32"}} onClick={()=>{ setBrokerSelected({ entity_name: brokerSearch, isNew: true }); }}>+ Add "{brokerSearch}" as new broker</div>
                </div>
              )}
            </div>
            {brokerSelected && <div style={selectedBadge}>{brokerSelected.isNew ? `➕ "${brokerSelected.entity_name}" — will be added to CRM` : `✅ ${brokerSelected.entity_name} — ${brokerSelected.city||''}, ${brokerSelected.state||''}`}</div>}
            <div style={sectionLabel}>PICKUP / SHIPPER</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="date" style={inputStyle} value={newLoad.pickup_date} onChange={e=>setNewLoad({...newLoad,pickup_date:e.target.value})} />
              <input type="text" placeholder="Pickup Hours" style={inputStyle} value={newLoad.shipper_hours} onChange={e=>setNewLoad({...newLoad,shipper_hours:e.target.value})} />
            </div>
            <div style={{ position:"relative" }}>
              <input type="text" placeholder="Search or type shipper name..." style={inputStyle} value={shipperSearch} onChange={e=>{setShipperSearch(e.target.value);setShipperSelected(null);setNewLoad({...newLoad,shipper_name:e.target.value});}} />
              {shipperSearch.length > 1 && !shipperSelected && (
                <div style={dropdown}>
                  {crmContacts.filter(c=>c.entity_name.toLowerCase().includes(shipperSearch.toLowerCase())).map(c=>(
                    <div key={c.id} style={dropdownItem} onClick={()=>{setShipperSelected(c);setShipperSearch(c.entity_name);setNewLoad({...newLoad,shipper_name:c.entity_name,shipper_address:c.address||c.address1||"",shipper_phone:c.phone||"",shipper_email:c.email||"",pickup_city:c.city||newLoad.pickup_city,pickup_state:c.state||newLoad.pickup_state});}}>{c.entity_name} <span style={{color:"#777"}}>({c.city}, {c.state})</span></div>
                  ))}
                  <div style={{...dropdownItem,color:"#32CD32"}} onClick={()=>setShipperSelected({ entity_name: shipperSearch, isNew: true })}>+ Add "{shipperSearch}" as new shipper</div>
                </div>
              )}
            </div>
            {shipperSelected && <div style={selectedBadge}>{shipperSelected.isNew ? `➕ "${shipperSelected.entity_name}" — will be added to CRM` : `✅ ${shipperSelected.entity_name} — ${shipperSelected.city||''}, ${shipperSelected.state||''}`}</div>}
            <input type="text" placeholder="Shipper Address" style={inputStyle} value={newLoad.shipper_address} onChange={e=>setNewLoad({...newLoad,shipper_address:e.target.value})} />
            <datalist id="ct-cities">{[...new Set(crmContacts.filter(c=>c.city).map(c=>c.city))].map(city=><option key={city} value={city}/>)}</datalist>
            <datalist id="ct-states">{US_STATES.map(s=><option key={s} value={s}/>)}</datalist>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Pickup City" list="ct-cities" style={inputStyle} value={newLoad.pickup_city} onChange={e=>setNewLoad({...newLoad,pickup_city:e.target.value})} />
              <input type="text" placeholder="Pickup State (e.g. TX)" list="ct-states" style={inputStyle} value={newLoad.pickup_state} onChange={e=>setNewLoad({...newLoad,pickup_state:e.target.value.toUpperCase()})} />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Shipper Phone" style={inputStyle} value={newLoad.shipper_phone} onChange={e=>setNewLoad({...newLoad,shipper_phone:e.target.value})} />
              <input type="email" placeholder="Shipper Email" style={inputStyle} value={newLoad.shipper_email} onChange={e=>setNewLoad({...newLoad,shipper_email:e.target.value})} />
            </div>
            <label style={{ fontSize:"12px", display:"flex", alignItems:"center", gap:"8px", color:"#aaa" }}>
              <input type="checkbox" checked={newLoad.shipper_appt_required} onChange={e=>setNewLoad({...newLoad,shipper_appt_required:e.target.checked})} />
              Appointment Required (Shipper)
            </label>
            <div style={sectionLabel}>DELIVERY / CONSIGNEE</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="date" style={inputStyle} value={newLoad.delivery_date} onChange={e=>setNewLoad({...newLoad,delivery_date:e.target.value})} />
              <input type="text" placeholder="Delivery Hours" style={inputStyle} value={newLoad.consignee_hours} onChange={e=>setNewLoad({...newLoad,consignee_hours:e.target.value})} />
            </div>
            <div style={{ position:"relative" }}>
              <input type="text" placeholder="Search or type consignee name..." style={inputStyle} value={consigneeSearch} onChange={e=>{setConsigneeSearch(e.target.value);setConsigneeSelected(null);setNewLoad({...newLoad,consignee_name:e.target.value});}} />
              {consigneeSearch.length > 1 && !consigneeSelected && (
                <div style={dropdown}>
                  {crmContacts.filter(c=>c.entity_name.toLowerCase().includes(consigneeSearch.toLowerCase())).map(c=>(
                    <div key={c.id} style={dropdownItem} onClick={()=>{setConsigneeSelected(c);setConsigneeSearch(c.entity_name);setNewLoad({...newLoad,consignee_name:c.entity_name,consignee_address:c.address||c.address1||"",consignee_phone:c.phone||"",consignee_email:c.email||"",delivery_city:c.city||newLoad.delivery_city,delivery_state:c.state||newLoad.delivery_state});}}>{c.entity_name} <span style={{color:"#777"}}>({c.city}, {c.state})</span></div>
                  ))}
                  <div style={{...dropdownItem,color:"#32CD32"}} onClick={()=>setConsigneeSelected({ entity_name: consigneeSearch, isNew: true })}>+ Add "{consigneeSearch}" as new consignee</div>
                </div>
              )}
            </div>
            {consigneeSelected && <div style={selectedBadge}>{consigneeSelected.isNew ? `➕ "${consigneeSelected.entity_name}" — will be added to CRM` : `✅ ${consigneeSelected.entity_name} — ${consigneeSelected.city||''}, ${consigneeSelected.state||''}`}</div>}
            <input type="text" placeholder="Consignee Address" style={inputStyle} value={newLoad.consignee_address} onChange={e=>setNewLoad({...newLoad,consignee_address:e.target.value})} />
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Delivery City" list="ct-cities" style={inputStyle} value={newLoad.delivery_city} onChange={e=>setNewLoad({...newLoad,delivery_city:e.target.value})} />
              <input type="text" placeholder="Delivery State (e.g. TX)" list="ct-states" style={inputStyle} value={newLoad.delivery_state} onChange={e=>setNewLoad({...newLoad,delivery_state:e.target.value.toUpperCase()})} />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Consignee Phone" style={inputStyle} value={newLoad.consignee_phone} onChange={e=>setNewLoad({...newLoad,consignee_phone:e.target.value})} />
              <input type="email" placeholder="Consignee Email" style={inputStyle} value={newLoad.consignee_email} onChange={e=>setNewLoad({...newLoad,consignee_email:e.target.value})} />
            </div>
            <label style={{ fontSize:"12px", display:"flex", alignItems:"center", gap:"8px", color:"#aaa" }}>
              <input type="checkbox" checked={newLoad.consignee_appt_required} onChange={e=>setNewLoad({...newLoad,consignee_appt_required:e.target.checked})} />
              Appointment Required (Consignee)
            </label>
            <div style={sectionLabel}>RATE & FINANCIALS</div>
            <input type="number" placeholder="Rate Total ($) *" style={inputStyle} value={newLoad.rate_total||""} onChange={e=>setNewLoad({...newLoad,rate_total:Number(e.target.value)})} />
            {(()=>{
              if (!newLoad.rate_total || !newLoad.loaded_miles) return null;
              const p = calcProfit(newLoad as any);
              const floorRate = (p.fuel_estimated_cost + p.driver_pay_estimated) * 1.10;
              if (newLoad.rate_total > 0 && newLoad.rate_total < floorRate) {
                return <div style={{ color:'#ff4444', fontSize:'11px', padding:'6px 10px', background:'rgba(255,68,68,0.08)', border:'1px solid #ff4444', marginTop:'4px' }}>
                  ⚠ This rate is below your floor of ${floorRate.toFixed(0)}. Your estimated margin is {p.profit_margin_percent.toFixed(1)}%.
                </div>;
              }
              return null;
            })()}
            <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr auto", gap:"6px", alignItems:"end" }}>
              <div>
                <div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Loaded Miles (pickup → delivery)</div>
                <input type="number" placeholder="Loaded Miles" style={inputStyle} value={newLoad.loaded_miles||""} onChange={e=>setNewLoad({...newLoad,loaded_miles:Number(e.target.value)})} />
              </div>
              <button type="button" onClick={async()=>{
                if (!newLoad.pickup_city||!newLoad.delivery_city) { alert("Enter pickup and delivery cities first."); return; }
                setCalcingMiles(true);
                const miles = await calcDistanceMiles(`${newLoad.pickup_city}, ${newLoad.pickup_state}`, `${newLoad.delivery_city}, ${newLoad.delivery_state}`);
                if (miles) setNewLoad({...newLoad, loaded_miles:miles});
                setCalcingMiles(false);
              }} style={{ padding:"8px 10px", fontFamily:"monospace", fontSize:"10px", cursor:"pointer", background:"#0a2a0a", border:"1px solid #32CD32", color:"#32CD32", alignSelf:"end", whiteSpace:"nowrap" }}>
                {calcingMiles?'...':'⟳ CALC'}
              </button>
              <div>
                <div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Deadhead Miles (driver → pickup)</div>
                <input type="number" placeholder="Deadhead Miles" style={inputStyle} value={newLoad.deadhead_miles||""} onChange={e=>setNewLoad({...newLoad,deadhead_miles:Number(e.target.value)})} />
              </div>
              <button type="button" onClick={async()=>{
                const driver = drivers.find((d:any)=>d.id===newLoad.assigned_driver_id);
                if (!driver?.current_city||!newLoad.pickup_city) { alert("Assign a driver with a known location and enter pickup city first."); return; }
                setCalcingMiles(true);
                const result = await calcDistanceWithDuration(`${driver.current_city}, ${driver.current_state}`, `${newLoad.pickup_city}, ${newLoad.pickup_state}`);
                if (result) {
                  setNewLoad({...newLoad, deadhead_miles:result.miles});
                  const h = Math.floor(result.secs/3600), m = Math.floor((result.secs%3600)/60);
                  setDriverEta(`ETA to pickup: ${h}h ${m}m from ${driver.current_city}, ${driver.current_state}`);
                }
                setCalcingMiles(false);
              }} style={{ padding:"8px 10px", fontFamily:"monospace", fontSize:"10px", cursor:"pointer", background:"#0a0a1a", border:"1px solid #4488FF", color:"#4488FF", alignSelf:"end", whiteSpace:"nowrap" }}>
                {calcingMiles?'...':'⟳ CALC'}
              </button>
            </div>
            {driverEta && <div style={{ color:"#4488FF", fontSize:"11px", padding:"6px 10px", background:"#0a0a1a", border:"1px solid #4488FF", marginTop:"4px" }}>{driverEta}</div>}
            {(newLoad.loaded_miles > 0 || newLoad.deadhead_miles > 0) && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px", padding:"8px", background:"#0a0a0a", border:"1px solid #1a1a1a", fontSize:"11px", color:"#aaa" }}>
                <span>TOTAL MILES: <strong style={{ color:"#fff" }}>{(newLoad.loaded_miles||0) + (newLoad.deadhead_miles||0)}</strong></span>
                {newLoad.rate_total > 0 && newLoad.loaded_miles > 0 && <span>RPM: <strong style={{ color:"#32CD32" }}>${(newLoad.rate_total/newLoad.loaded_miles).toFixed(2)}</strong></span>}
                {newLoad.rate_total > 0 && newLoad.loaded_miles > 0 && <span>EST. PROFIT: <strong style={{ color: calcProfit(newLoad).band==='GREEN'?'#32CD32':calcProfit(newLoad).band==='YELLOW'?'#FFD700':'#ff4444' }}>${calcProfit(newLoad).net_profit.toFixed(0)} ({calcProfit(newLoad).profit_margin_percent.toFixed(1)}%)</strong></span>}
              </div>
            )}
            <div style={sectionLabel}>SPECIAL INSTRUCTIONS</div>
            <textarea placeholder="Special handling, access codes, driver notes..." style={{...inputStyle,resize:"vertical"} as any} rows={3} value={newLoad.special_instructions} onChange={e=>setNewLoad({...newLoad,special_instructions:e.target.value})} />
            {validationWarnings.length > 0 && (
              <div style={{ background:"rgba(255,215,0,0.07)", border:"1px solid #FFD700", padding:"12px 16px", marginTop:"10px" }}>
                <div style={{ color:"#FFD700", fontSize:"10px", fontWeight:700, letterSpacing:"2px", marginBottom:"8px" }}>⚡ JAKE: LOAD IS INCOMPLETE — saved but flagged</div>
                {validationWarnings.map((w,i)=>(
                  <div key={i} style={{ fontSize:"11px", color:"#aaa", padding:"3px 0", borderBottom:"1px solid #1a1a1a" }}>
                    <span style={{ color:"#FFD700", fontWeight:700 }}>{w.field}:</span> {w.message}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
              <button onClick={handleSave} style={bigBtn}>SAVE TO SYSTEM</button>
              <button onClick={()=>{setShowEntryForm(false);resetLoadForm();setValidationWarnings([]);}} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOAD DETAIL VIEW ── */}
      {selectedLoad && (
        <div style={card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
            <button onClick={()=>setSelectedLoad(null)} style={{ color:"#32CD32", background:"none", border:"none", cursor:"pointer", fontSize:"13px" }}>{"← BACK TO LOAD BOARD"}</button>
            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={handleExportInvoice} style={{...smallBtn,background:"#1a3a1a",color:"#32CD32",border:"1px solid #32CD32"}}>📄 INVOICE PDF</button>
              <button onClick={handleExportRateConf} style={{...smallBtn,background:"#1a3a1a",color:"#32CD32",border:"1px solid #32CD32"}}>📋 RATE CONF PDF</button>
              <button onClick={handleExportSettlement} style={{...smallBtn,background:"#1a3a1a",color:"#32CD32",border:"1px solid #32CD32"}}>💵 SETTLEMENT PDF</button>
              <a href="https://www.otrcapital.com/" target="_blank" rel="noopener noreferrer" style={{...smallBtn,background:"#1a1a3a",color:"#4488FF",border:"1px solid #4488FF",textDecoration:"none",display:"inline-block"}}>💳 OTR FACTORING</a>
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
            <h3 style={{ margin:0 }}>LOAD: {selectedLoad.load_number||"---"} <span style={{ color:"#32CD32" }}>({selectedLoad.status||"ACTIVE"})</span></h3>
            <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
              {statusConfirm && <span style={{ color:"#32CD32", fontSize:"11px", fontWeight:700 }}>{statusConfirm}</span>}
              {['ACTIVE','PENDING'].includes((selectedLoad.status||'').toUpperCase()) && (
                <button onClick={()=>updateLoadStatus('IN TRANSIT')} style={{ ...smallBtn, background:"#4488FF", color:"#fff", border:"none" }}>→ MARK IN TRANSIT</button>
              )}
              {(selectedLoad.status||'').toUpperCase() === 'IN TRANSIT' && (
                <button onClick={()=>updateLoadStatus('DELIVERED')} style={{ ...smallBtn, background:"#32CD32", color:"#000" }}>✓ MARK DELIVERED</button>
              )}
              {selectedLoad.tracking_token && (
                <button onClick={()=>{ navigator.clipboard.writeText(`https://app.blacktopsystem.com/track?token=${selectedLoad.tracking_token}`); showConfirm('✓ Tracking link copied'); }} style={{ ...smallBtn, background:"transparent", border:"1px solid #555", color:"#aaa" }}>🔗 TRACKING LINK</button>
              )}
              {selectedLoad.status === 'DELIVERED' && !selectedLoad.factoring_status && (
                <button onClick={async ()=>{ const { error } = await supabase.functions.invoke('factoring-submit', { body:{ loadId:selectedLoad.id, companyId } }); showConfirm(error ? '✗ Factoring failed' : '✓ Submitted to factor'); }} style={{ ...smallBtn, background:"transparent", border:"1px solid #32CD32", color:"#32CD32" }}>$ FACTOR</button>
              )}
              {selectedLoad.factoring_status && (
                <span style={{ fontSize:"10px", color:"#32CD32", fontWeight:700, padding:"8px" }}>$ {selectedLoad.factoring_status}</span>
              )}
            </div>
          </div>

          {/* WORKFLOW TRACKER */}
          <div style={{ background:"#050505", border:"1px solid #1e1e1e", padding:"16px", marginBottom:"16px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700 }}>LOAD LIFECYCLE</div>
              <div style={{ display:"flex", gap:"6px" }}>
                <button onClick={()=>updateLoadStatus('CANCELLED')} style={{ padding:"4px 10px", fontFamily:"monospace", fontSize:"9px", fontWeight:700, cursor:"pointer", border:"1px solid #ff4444", background:selectedLoad.status==='CANCELLED'?'#ff4444':"transparent", color:selectedLoad.status==='CANCELLED'?'#000':'#ff4444' }}>CANCEL</button>
              </div>
            </div>
            <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
              {WORKFLOW_STAGES.map(stage=>{
                const isActive = selectedLoad.workflow_status === stage.key || (!selectedLoad.workflow_status && stage.key === 'BOOKED' && selectedLoad.status === 'ACTIVE');
                return (
                  <button key={stage.key} onClick={()=>updateWorkflowStatus(stage.key)}
                    style={{ padding:"8px 12px", fontFamily:"monospace", fontSize:"10px", fontWeight:700, letterSpacing:"0.5px", cursor:"pointer", border:`1px solid ${stage.color}`,
                      background:isActive?stage.color:"transparent", color:isActive?'#000':stage.color }}>
                    {stage.label}
                  </button>
                );
              })}
            </div>
            {selectedLoad.workflow_status && (
              <div style={{ marginTop:"10px", fontSize:"11px", color:"#555" }}>
                CURRENT: <span style={{ color:WORKFLOW_STAGES.find(s=>s.key===selectedLoad.workflow_status)?.color||'#aaa', fontWeight:700 }}>
                  {WORKFLOW_STAGES.find(s=>s.key===selectedLoad.workflow_status)?.label||selectedLoad.workflow_status}
                </span>
                {selectedLoad.dispatched_at && <span style={{ marginLeft:"16px" }}>DISPATCHED: {new Date(selectedLoad.dispatched_at).toLocaleString()}</span>}
                {selectedLoad.delivered_at && <span style={{ marginLeft:"16px", color:"#32CD32" }}>DELIVERED: {new Date(selectedLoad.delivered_at).toLocaleString()}</span>}
              </div>
            )}
          </div>

          {/* JAKE RATE ADVISOR */}
          <RateNegotiatorPanel
            load={selectedLoad}
            driver={drivers.find((d:any)=>d.id===selectedLoad.assigned_driver_id) || null}
            companyId={companyId}
          />

          {/* PROFIT ANALYSIS */}
          {(()=>{
            const driver = drivers.find(d=>d.id===selectedLoad.assigned_driver_id);
            const p = calcProfit(selectedLoad, driver);
            const bandColor = p.band==='RED'?'#ff4444':p.band==='YELLOW'?'#FFD700':'#32CD32';
            return (
              <div style={{ background:"#050505", border:`1px solid ${bandColor}`, padding:"16px", marginBottom:"16px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
                  <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700 }}>PROFIT ANALYSIS</div>
                  <span style={{ fontSize:"10px", fontWeight:700, color:bandColor, border:`1px solid ${bandColor}`, padding:"2px 8px" }}>{p.band}</span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"12px" }}>
                  {[
                    {label:"GROSS REVENUE",value:`$${p.revenue.toLocaleString()}`,color:"#ccc"},
                    {label:"NET PROFIT",value:`$${p.net_profit.toFixed(0)}`,color:bandColor},
                    {label:"MARGIN",value:`${p.profit_margin_percent.toFixed(1)}%`,color:bandColor},
                    {label:"RPM",value:`$${p.rpm.toFixed(2)}`,color:p.rpm>=2?'#32CD32':p.rpm>=1.5?'#FFD700':'#ff4444'},
                    {label:"LOADED MILES",value:p.loaded_miles.toLocaleString(),color:"#aaa"},
                    {label:"TOTAL MILES",value:p.total_miles.toLocaleString(),color:"#aaa"},
                    {label:"EST. FUEL COST",value:`$${p.fuel_estimated_cost.toFixed(0)}`,color:"#aaa"},
                    {label:"DRIVER PAY EST.",value:`$${p.driver_pay_estimated.toFixed(0)}`,color:"#aaa"},
                  ].map((item,i)=>(
                    <div key={i} style={{ background:"#0a0a0a", padding:"10px", border:"1px solid #1a1a1a" }}>
                      <div style={{ color:"#555", fontSize:"9px", letterSpacing:"1px", marginBottom:"4px" }}>{item.label}</div>
                      <div style={{ color:item.color, fontSize:"16px", fontWeight:700 }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ASSIGN DRIVER & ASSET */}
          <div style={{ background:"#0d0d0d", border:"1px solid #1e1e1e", padding:"16px", marginBottom:"16px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700 }}>ASSIGN DRIVER & ASSET</div>
              <button onClick={()=>suggestDriverForLoad(selectedLoad)} style={{ background:"#1a1a00", border:"1px solid #FFD700", color:"#FFD700", padding:"4px 10px", fontFamily:"monospace", fontSize:"9px", fontWeight:700, cursor:"pointer", letterSpacing:"1px" }}>
                {jakeSuggLoading ? '⏳ ANALYZING...' : '⚡ JAKE SUGGEST'}
              </button>
            </div>
            {jakeSuggestion && (
              <div style={{ background:"#0a0a00", border:"1px solid #FFD700", padding:"10px", marginBottom:"12px" }}>
                <div style={{ color:"#FFD700", fontSize:"9px", letterSpacing:"1px", marginBottom:"4px" }}>JAKE RECOMMENDS</div>
                <div style={{ color:"#fff", fontWeight:700, fontSize:"13px" }}>{jakeSuggestion.driver.full_name}</div>
                <div style={{ color:"#aaa", fontSize:"10px", marginTop:"2px" }}>{jakeSuggestion.reason}</div>
                <button onClick={async()=>{
                  if (!selectedLoad?.id) return;
                  const d = jakeSuggestion.driver;
                  const p = calcProfit(selectedLoad, d);
                  const { data: updated, error } = await supabase.from('loads').update({ assigned_driver_id:d.id, driver_pay_estimated:p.driver_pay_estimated, net_profit:p.net_profit, profit_margin_percent:p.profit_margin_percent }).eq('id', selectedLoad.id).select('*').single();
                  if (!error && updated) { setSelectedLoad(updated); fetchRoomData(); setJakeSuggestion(null); }
                }} style={{ marginTop:"8px", background:"#FFD700", color:"#000", border:"none", padding:"6px 14px", fontFamily:"monospace", fontSize:"10px", fontWeight:700, cursor:"pointer" }}>ASSIGN {jakeSuggestion.driver.full_name.toUpperCase()}</button>
                <button onClick={()=>setJakeSuggestion(null)} style={{ marginTop:"8px", marginLeft:"8px", background:"transparent", color:"#555", border:"1px solid #333", padding:"6px 10px", fontFamily:"monospace", fontSize:"10px", cursor:"pointer" }}>DISMISS</button>
              </div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <div>
                <div style={{ color:"#666", fontSize:"11px", marginBottom:"6px" }}>DRIVER</div>
                <select style={{ background:"#000", border:"1px solid #333", color:"#fff", padding:"10px", fontFamily:"monospace", width:"100%" }}
                  value={selectedLoad?.assigned_driver_id||""}
                  onChange={async e=>{
                    if (!selectedLoad?.id) return;
                    const driverId = e.target.value||null;
                    const driver = drivers.find(d=>d.id===driverId);
                    const p = calcProfit(selectedLoad, driver);
                    const { data: updated, error } = await supabase.from('loads').update({ assigned_driver_id:driverId, driver_pay_estimated:p.driver_pay_estimated, net_profit:p.net_profit, profit_margin_percent:p.profit_margin_percent }).eq('id', selectedLoad.id).select('*').single();
                    if (!error && updated) {
                      setSelectedLoad(updated);
                      setEditDraft((prev:any) => prev ? { ...prev, assigned_driver_id: driverId } : prev);
                      fetchRoomData();
                      // Calculate deadhead miles + ETA from driver location → pickup
                      if (driver?.current_city && driver?.current_state && updated.pickup_city && updated.pickup_state) {
                        const origin = `${driver.current_city}, ${driver.current_state}`;
                        const dest = `${updated.pickup_city}, ${updated.pickup_state}`;
                        const result = await calcDistanceWithDuration(origin, dest);
                        if (result) {
                          const deadhead = result.miles;
                          const etaHours = Math.floor(result.secs / 3600);
                          const etaMins = Math.floor((result.secs % 3600) / 60);
                          setDriverEta(`ETA to pickup: ${etaHours}h ${etaMins}m (${deadhead} mi deadhead)`);
                          // Save deadhead miles to load
                          const { data: u2 } = await supabase.from('loads').update({ deadhead_miles: deadhead }).eq('id', selectedLoad.id).select('*').single();
                          if (u2) { setSelectedLoad(u2); setEditDraft((prev:any) => prev ? { ...prev, deadhead_miles: deadhead } : prev); }
                        }
                      } else {
                        setDriverEta(null);
                      }
                    }
                  }}>
                  <option value="">-- No Driver --</option>
                  {drivers.map(d=><option key={d.id} value={d.id}>{d.full_name} [{d.hos_status||'OFF DUTY'}] — {d.current_city||'?'}, {d.current_state||'?'}</option>)}
                </select>
                {driverEta && <div style={{ color:"#4488FF", fontSize:"10px", marginTop:"6px", padding:"4px 8px", background:"#0a0a1a", border:"1px solid #4488FF" }}>{driverEta}</div>}
                {(()=>{
                  const d = drivers.find((x:any)=>x.id===selectedLoad?.assigned_driver_id);
                  const pay = selectedLoad?.driver_pay_estimated;
                  if (!d || !pay) return null;
                  const payRate = d.pay_type==='PERCENT'?`${d.pay_rate_percent||d.pay_percent||0}% of rate`:d.pay_type==='PER_MILE'?`$${d.pay_rate_per_mile||0}/mi`:'Flat';
                  return <div style={{ color:"#32CD32", fontSize:"10px", marginTop:"4px" }}>Driver Pay: <strong>${Number(pay).toFixed(2)}</strong> ({payRate})</div>;
                })()}
              </div>
              <div>
                <div style={{ color:"#666", fontSize:"11px", marginBottom:"6px" }}>POWER UNIT / TRUCK</div>
                <select style={{ background:"#000", border:"1px solid #333", color:"#fff", padding:"10px", fontFamily:"monospace", width:"100%" }}
                  value={selectedLoad?.assigned_asset_id||""}
                  onChange={async e=>{
                    if (!selectedLoad?.id) return;
                    const assetId = e.target.value||null;
                    const { data: updated, error } = await supabase.from('loads').update({ assigned_asset_id:assetId }).eq('id', selectedLoad.id).select('*').single();
                    if (!error && updated) { setSelectedLoad(updated); setEditDraft((prev:any) => prev ? { ...prev, assigned_asset_id: assetId } : prev); fetchRoomData(); }
                  }}>
                  <option value="">-- No Truck --</option>
                  {assets.filter(a=>!/trailer/i.test(a.asset_type||'')).map(a=><option key={a.id} value={a.id}>{a.unit_number} — {a.asset_type} [{a.current_status}]</option>)}
                </select>
              </div>
            </div>
            {/* TRAILER — second row, full width */}
            <div style={{ marginTop:"10px" }}>
              <div style={{ color:"#666", fontSize:"11px", marginBottom:"6px" }}>TRAILER <span style={{ color:"#444", fontSize:"10px" }}>(semi loads)</span></div>
              <select style={{ background:"#000", border:"1px solid #333", color:"#fff", padding:"10px", fontFamily:"monospace", width:"100%" }}
                value={selectedLoad?.assigned_trailer_id||""}
                onChange={async e=>{
                  if (!selectedLoad?.id) return;
                  const trailerId = e.target.value||null;
                  const { data: updated, error } = await supabase.from('loads').update({ assigned_trailer_id:trailerId }).eq('id', selectedLoad.id).select('*').single();
                  if (!error && updated) { setSelectedLoad(updated); setEditDraft((prev:any) => prev ? { ...prev, assigned_trailer_id: trailerId } : prev); fetchRoomData(); }
                }}>
                <option value="">-- No Trailer --</option>
                {(assets.filter(a=>/trailer/i.test(a.asset_type||'')).length > 0
                  ? assets.filter(a=>/trailer/i.test(a.asset_type||''))
                  : assets
                ).map(a=><option key={a.id} value={a.id}>{a.unit_number} — {a.asset_type} [{a.current_status}]</option>)}
              </select>
            </div>
          </div>

          {/* INLINE EDIT SAVE BAR */}
          {editDraft && (
            <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"16px", padding:"12px 16px", background:"#0a0a0a", border:"1px solid #32CD32" }}>
              <span style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"1px", flex:1 }}>✏️ EDITING LOAD — All fields below are editable. Click SAVE when done.</span>
              {editSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
              <button onClick={saveEditDraft} disabled={editSaving} style={{ ...smallBtn, background:editSaving?"#1a1a1a":"#32CD32", color:editSaving?"#555":"#000" }}>{editSaving?"SAVING...":"SAVE CHANGES"}</button>
            </div>
          )}

          {/* LOAD INFO GRID */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" }}>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>LOAD INFO</div>
              <div style={fieldRow}><span style={fieldLabel}>CUSTOMER</span><input style={inlineInput} value={editDraft?.customer_name||""} onChange={e=>setEditDraft({...editDraft,customer_name:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>EQUIPMENT</span>
                <select style={inlineInput as any} value={editDraft?.equipment_type||""} onChange={e=>setEditDraft({...editDraft,equipment_type:e.target.value})}>
                  <option value="">Select...</option>
                  {["Box Truck","Cargo Van","Sprinter","Flatbed","Dry Van","Reefer","Other"].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>COMMODITY</span><input style={inlineInput} value={editDraft?.commodity||""} onChange={e=>setEditDraft({...editDraft,commodity:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>WEIGHT (lbs)</span><input type="number" style={inlineInput} value={editDraft?.weight_lbs||""} onChange={e=>setEditDraft({...editDraft,weight_lbs:Number(e.target.value)})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>PO / REF #</span><input style={inlineInput} value={editDraft?.po_number||""} onChange={e=>setEditDraft({...editDraft,po_number:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>BOL #</span><input style={inlineInput} value={editDraft?.bol_number||""} onChange={e=>setEditDraft({...editDraft,bol_number:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>RATE ($)</span><input type="number" style={inlineInput} value={editDraft?.rate_total||""} onChange={e=>setEditDraft({...editDraft,rate_total:Number(e.target.value)})} /></div>
              <div style={fieldRow}>
                <span style={fieldLabel}>LOADED MILES</span>
                <div style={{ display:"flex", gap:"6px", alignItems:"center", flex:1 }}>
                  <input type="number" style={{ ...inlineInput, flex:1 }} value={editDraft?.loaded_miles||""} onChange={e=>setEditDraft({...editDraft,loaded_miles:Number(e.target.value)})} />
                  <button onClick={async()=>{
                    if (!editDraft?.pickup_city||!editDraft?.delivery_city) return;
                    setCalcingMiles(true);
                    const origin = `${editDraft.pickup_city}, ${editDraft.pickup_state||''}`.trim();
                    const dest = `${editDraft.delivery_city}, ${editDraft.delivery_state||''}`.trim();
                    const miles = await calcDistanceMiles(origin, dest);
                    if (miles) setEditDraft((d:any)=>({...d,loaded_miles:miles}));
                    setCalcingMiles(false);
                  }} style={{ padding:"4px 8px", fontFamily:"monospace", fontSize:"9px", cursor:"pointer", background:"#1a1a1a", border:"1px solid #32CD32", color:"#32CD32", whiteSpace:"nowrap" }}>
                    {calcingMiles?'...':'⟳ CALC'}
                  </button>
                </div>
              </div>
              <div style={fieldRow}>
                <span style={fieldLabel}>DEADHEAD MILES</span>
                <div style={{ display:"flex", gap:"6px", alignItems:"center", flex:1 }}>
                  <input type="number" style={{ ...inlineInput, flex:1 }} value={editDraft?.deadhead_miles||""} onChange={e=>setEditDraft({...editDraft,deadhead_miles:Number(e.target.value)})} />
                  <button onClick={async()=>{
                    const driver = drivers.find((d:any)=>d.id===editDraft?.assigned_driver_id);
                    if (!driver?.current_city||!editDraft?.pickup_city) return;
                    setCalcingMiles(true);
                    const origin = `${driver.current_city}, ${driver.current_state||''}`.trim();
                    const dest = `${editDraft.pickup_city}, ${editDraft.pickup_state||''}`.trim();
                    const result = await calcDistanceWithDuration(origin, dest);
                    if (result) {
                      setEditDraft((d:any)=>({...d,deadhead_miles:result.miles}));
                      const h = Math.floor(result.secs/3600), m = Math.floor((result.secs%3600)/60);
                      setDriverEta(`ETA to pickup: ${h}h ${m}m (${result.miles} mi deadhead from ${driver.current_city}, ${driver.current_state})`);
                    }
                    setCalcingMiles(false);
                  }} style={{ padding:"4px 8px", fontFamily:"monospace", fontSize:"9px", cursor:"pointer", background:"#1a1a1a", border:"1px solid #4488FF", color:"#4488FF", whiteSpace:"nowrap" }}>
                    {calcingMiles?'...':'⟳ CALC'}
                  </button>
                </div>
              </div>
            </div>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>ROUTE</div>
              <div style={fieldRow}><span style={fieldLabel}>PICKUP DATE</span><input type="date" style={inlineInput} value={editDraft?.pickup_date||""} onChange={e=>setEditDraft({...editDraft,pickup_date:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>PICKUP CITY</span><input style={inlineInput} value={editDraft?.pickup_city||""} onChange={e=>setEditDraft({...editDraft,pickup_city:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>PICKUP STATE</span><input style={inlineInput} value={editDraft?.pickup_state||""} onChange={e=>setEditDraft({...editDraft,pickup_state:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>DELIVERY DATE</span><input type="date" style={inlineInput} value={editDraft?.delivery_date||""} onChange={e=>setEditDraft({...editDraft,delivery_date:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>DELIVERY CITY</span><input style={inlineInput} value={editDraft?.delivery_city||""} onChange={e=>setEditDraft({...editDraft,delivery_city:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>DELIVERY STATE</span><input style={inlineInput} value={editDraft?.delivery_state||""} onChange={e=>setEditDraft({...editDraft,delivery_state:e.target.value})} /></div>
              {selectedLoad.dispatched_at && <div style={fieldRow}><span style={fieldLabel}>DISPATCHED</span><span style={{...fieldVal,color:"#4488FF"}}>{new Date(selectedLoad.dispatched_at).toLocaleString()}</span></div>}
              {selectedLoad.delivered_at && <div style={fieldRow}><span style={fieldLabel}>DELIVERED</span><span style={{...fieldVal,color:"#32CD32"}}>{new Date(selectedLoad.delivered_at).toLocaleString()}</span></div>}
            </div>
          </div>

          {/* SHIPPER / CONSIGNEE */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" }}>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>SHIPPER</div>
              <div style={{ ...fieldRow, position:"relative" }}>
                <span style={fieldLabel}>NAME</span>
                <div style={{ flex:1, position:"relative" }}>
                  <input style={inlineInput} value={editDraft?.shipper_name||""} onChange={e=>setEditDraft({...editDraft,shipper_name:e.target.value})} placeholder="Type or search CRM..." />
                  {(editDraft?.shipper_name||"").length > 1 && (()=>{
                    const matches = crmContacts.filter((c:any)=>c.entity_name.toLowerCase().includes((editDraft.shipper_name||"").toLowerCase()));
                    if (!matches.length) return null;
                    return <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#111", border:"1px solid #32CD32", zIndex:50, maxHeight:"120px", overflowY:"auto" }}>
                      {matches.map((c:any)=>(
                        <div key={c.id} style={{ padding:"6px 10px", cursor:"pointer", fontSize:"11px", borderBottom:"1px solid #0a0a0a" }} onMouseDown={()=>setEditDraft({...editDraft,shipper_name:c.entity_name,shipper_address:c.address||c.address1||"",shipper_phone:c.phone||"",shipper_email:c.email||"",pickup_city:c.city||editDraft.pickup_city,pickup_state:c.state||editDraft.pickup_state})}>
                          {c.entity_name} <span style={{color:"#555"}}>{c.city}, {c.state}</span>
                        </div>
                      ))}
                    </div>;
                  })()}
                </div>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>STREET ADDR</span><input style={inlineInput} value={editDraft?.shipper_address||""} onChange={e=>setEditDraft({...editDraft,shipper_address:e.target.value})} placeholder="123 Main St" /></div>
              <div style={fieldRow}><span style={fieldLabel}>CITY</span><input style={inlineInput} value={editDraft?.pickup_city||""} onChange={e=>setEditDraft({...editDraft,pickup_city:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>STATE</span>
                <select style={inlineInput as any} value={editDraft?.pickup_state||""} onChange={e=>setEditDraft({...editDraft,pickup_state:e.target.value})}>
                  <option value="">—</option>{US_STATES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>PHONE</span><input style={inlineInput} value={editDraft?.shipper_phone||""} onChange={e=>setEditDraft({...editDraft,shipper_phone:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>EMAIL</span><input style={inlineInput} value={editDraft?.shipper_email||""} onChange={e=>setEditDraft({...editDraft,shipper_email:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>HOURS</span><input style={inlineInput} value={editDraft?.shipper_hours||""} onChange={e=>setEditDraft({...editDraft,shipper_hours:e.target.value})} placeholder="e.g. 7am–4pm" /></div>
              <div style={fieldRow}><span style={fieldLabel}>APPT REQ</span>
                <select style={inlineInput as any} value={editDraft?.shipper_appt_required?"yes":"no"} onChange={e=>setEditDraft({...editDraft,shipper_appt_required:e.target.value==="yes"})}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
            </div>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>CONSIGNEE</div>
              <div style={{ ...fieldRow, position:"relative" }}>
                <span style={fieldLabel}>NAME</span>
                <div style={{ flex:1, position:"relative" }}>
                  <input style={inlineInput} value={editDraft?.consignee_name||""} onChange={e=>setEditDraft({...editDraft,consignee_name:e.target.value})} placeholder="Type or search CRM..." />
                  {(editDraft?.consignee_name||"").length > 1 && (()=>{
                    const matches = crmContacts.filter((c:any)=>c.entity_name.toLowerCase().includes((editDraft.consignee_name||"").toLowerCase()));
                    if (!matches.length) return null;
                    return <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#111", border:"1px solid #32CD32", zIndex:50, maxHeight:"120px", overflowY:"auto" }}>
                      {matches.map((c:any)=>(
                        <div key={c.id} style={{ padding:"6px 10px", cursor:"pointer", fontSize:"11px", borderBottom:"1px solid #0a0a0a" }} onMouseDown={()=>setEditDraft({...editDraft,consignee_name:c.entity_name,consignee_address:c.address||c.address1||"",consignee_phone:c.phone||"",consignee_email:c.email||"",delivery_city:c.city||editDraft.delivery_city,delivery_state:c.state||editDraft.delivery_state})}>
                          {c.entity_name} <span style={{color:"#555"}}>{c.city}, {c.state}</span>
                        </div>
                      ))}
                    </div>;
                  })()}
                </div>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>STREET ADDR</span><input style={inlineInput} value={editDraft?.consignee_address||""} onChange={e=>setEditDraft({...editDraft,consignee_address:e.target.value})} placeholder="123 Main St" /></div>
              <div style={fieldRow}><span style={fieldLabel}>CITY</span><input style={inlineInput} value={editDraft?.delivery_city||""} onChange={e=>setEditDraft({...editDraft,delivery_city:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>STATE</span>
                <select style={inlineInput as any} value={editDraft?.delivery_state||""} onChange={e=>setEditDraft({...editDraft,delivery_state:e.target.value})}>
                  <option value="">—</option>{US_STATES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>PHONE</span><input style={inlineInput} value={editDraft?.consignee_phone||""} onChange={e=>setEditDraft({...editDraft,consignee_phone:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>EMAIL</span><input style={inlineInput} value={editDraft?.consignee_email||""} onChange={e=>setEditDraft({...editDraft,consignee_email:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>HOURS</span><input style={inlineInput} value={editDraft?.consignee_hours||""} onChange={e=>setEditDraft({...editDraft,consignee_hours:e.target.value})} placeholder="e.g. 8am–5pm" /></div>
              <div style={fieldRow}><span style={fieldLabel}>APPT REQ</span>
                <select style={inlineInput as any} value={editDraft?.consignee_appt_required?"yes":"no"} onChange={e=>setEditDraft({...editDraft,consignee_appt_required:e.target.value==="yes"})}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </div>

          {/* SPECIAL INSTRUCTIONS */}
          <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0a0a0a", marginBottom:"16px" }}>
            <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"8px" }}>SPECIAL INSTRUCTIONS</div>
            <textarea style={{ ...inlineInput, resize:"vertical", minHeight:"60px" } as any} value={editDraft?.special_instructions||""} onChange={e=>setEditDraft({...editDraft,special_instructions:e.target.value})} placeholder="Special instructions, hazmat notes, appointment details..." />
          </div>

          {/* TRACKING & NOTES */}
          <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0a0a0a" }}>
            <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"12px" }}>TRACKING & NOTES</div>
            {notesLoading ? <p style={{ color:"#aaa" }}>Loading notes...</p> :
              loadNotes.length === 0 ? <p style={{ color:"#555", fontSize:"12px" }}>No notes yet. Add the first tracking update below.</p> :
              <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginBottom:"12px" }}>
                {loadNotes.map(n=>(
                  <div key={n.id} style={{ border:"1px solid #1a1a1a", background:"#050505", padding:"10px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <div style={{ color:n.author_type==='jake'?"#FFD700":"#32CD32", fontSize:"10px" }}>{(n.author_type||"user").toUpperCase()}{n.author_label?` — ${n.author_label}`:""}</div>
                      <div style={{ color:"#555", fontSize:"10px" }}>{n.created_at?new Date(n.created_at).toLocaleString():""}</div>
                    </div>
                    <div style={{ marginTop:"6px", whiteSpace:"pre-wrap", lineHeight:"1.4", fontSize:"12px" }}>{n.note}</div>
                  </div>
                ))}
              </div>
            }
            <div style={{ borderTop:"1px solid #1a1a1a", paddingTop:"12px" }}>
              <textarea value={noteDraft} onChange={e=>setNoteDraft(e.target.value)} placeholder="Type ops notes, appointment updates, broker/customer details, delays..." rows={3} style={{ width:"100%", background:"#000", border:"1px solid #1a1a1a", color:"#fff", padding:"10px", fontFamily:"monospace", resize:"vertical", boxSizing:"border-box" }} />
              <div style={{ display:"flex", justifyContent:"flex-end", marginTop:"8px" }}>
                <button onClick={addLoadNote} style={smallBtn}>SAVE NOTE</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LOAD BOARD (unified filtered + sortable) ── */}
      {!selectedLoad && (()=>{
        // Filter
        const q = search.toLowerCase();
        let rows = data.filter(item => {
          if (q && !((item.load_number||'').toLowerCase().includes(q) || (item.customer_name||'').toLowerCase().includes(q))) return false;
          if (statusFilter !== 'ALL' && (item.status||'').toUpperCase() !== statusFilter) return false;
          if (bandFilter !== 'ALL' && (item.profit_band||'') !== bandFilter) return false;
          return true;
        });

        // Sort
        rows = [...rows].sort((a, b) => {
          let av: any, bv: any;
          if (sortCol === 'driver') {
            av = drivers.find(d=>d.id===a.assigned_driver_id)?.full_name||'';
            bv = drivers.find(d=>d.id===b.assigned_driver_id)?.full_name||'';
          } else if (sortCol === 'rpm') {
            av = Number(a.rpm||0); bv = Number(b.rpm||0);
          } else if (sortCol === 'profit_margin_percent') {
            av = Number(a.profit_margin_percent||0); bv = Number(b.profit_margin_percent||0);
          } else if (sortCol === 'rate_total') {
            av = Number(a.rate_total||0); bv = Number(b.rate_total||0);
          } else {
            av = (a[sortCol]||'').toString().toLowerCase();
            bv = (b[sortCol]||'').toString().toLowerCase();
          }
          if (av < bv) return sortDir === 'asc' ? -1 : 1;
          if (av > bv) return sortDir === 'asc' ? 1 : -1;
          return 0;
        });

        const toggleSort = (col: string) => {
          if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
          else { setSortCol(col); setSortDir('asc'); }
        };
        const sortIndicator = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
        const sortableTh = (col: string, label: string) => (
          <th onClick={()=>toggleSort(col)} style={{ ...thStyle, cursor:'pointer', userSelect:'none' }}>{label}{sortIndicator(col)}</th>
        );

        const statusColor = (s:string) => s==='IN TRANSIT'?'#4488FF':s==='ACTIVE'?'#FFD700':s==='DELIVERED'?'#32CD32':s==='CANCELLED'?'#555':'#aaa';
        const bandColor = (b:string) => b==='RED'?'#ff4444':b==='YELLOW'?'#FFD700':'#32CD32';
        const filterInput: React.CSSProperties = { background:"#0a0a0a", border:"1px solid #2a2a2a", color:"#fff", padding:"6px 10px", fontFamily:"monospace", fontSize:"11px" };

        return (
          <div>
            {/* Filter bar */}
            <div style={{ display:"flex", gap:"10px", marginBottom:"16px", alignItems:"center", flexWrap:"wrap" }}>
              <input placeholder="Search load # or customer..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{ ...filterInput, width:"220px" }} />
              <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={filterInput as any}>
                {['ALL','ACTIVE','PENDING','IN TRANSIT','DELIVERED','CANCELLED'].map(s=><option key={s} value={s}>{s === 'ALL' ? 'ALL STATUSES' : s}</option>)}
              </select>
              <select value={bandFilter} onChange={e=>setBandFilter(e.target.value)} style={filterInput as any}>
                {['ALL','GREEN','YELLOW','RED'].map(b=><option key={b} value={b}>{b === 'ALL' ? 'ALL BANDS' : b}</option>)}
              </select>
              <span style={{ color:"#555", fontSize:"11px", marginLeft:"auto" }}>{rows.length} load{rows.length!==1?'s':''}</span>
            </div>

            {rows.length === 0 ? (
              <div style={{ color:"#444", fontSize:"12px", padding:"16px", border:"1px solid #1a1a1a" }}>No loads match the current filters.</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {sortableTh('load_number','LOAD #')}
                    {sortableTh('customer_name','CUSTOMER')}
                    {sortableTh('pickup_date','PICKUP')}
                    {sortableTh('delivery_date','DELIVERY')}
                    {sortableTh('status','STATUS')}
                    {sortableTh('driver','DRIVER')}
                    {sortableTh('rate_total','RATE')}
                    <th style={thStyle}>DRIVER PAY</th>
                    <th style={thStyle}>EST FUEL</th>
                    {sortableTh('profit_margin_percent','NET PROFIT')}
                    <th style={thStyle}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item,idx)=>{
                    const driver = drivers.find(d=>d.id===item.assigned_driver_id);
                    const p = calcProfit(item, driver);
                    const bc = bandColor(item.profit_band||p.band);
                    const sc = statusColor(item.status||'');
                    return (
                      <tr key={idx} style={trStyle} onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background='#0d0d0d'} onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background=''}>
                        <td style={{ padding:"10px 12px", fontWeight:700, color:"#32CD32", cursor:"pointer" }} onClick={()=>openDrillDown(item)}>{item.load_number||"---"}</td>
                        <td style={{ padding:"10px 12px" }}>{item.customer_name||"---"}</td>
                        <td style={{ padding:"10px 12px", fontSize:"11px" }}>{item.pickup_city&&item.pickup_state?`${item.pickup_city}, ${item.pickup_state}`:''}{item.pickup_date?<div style={{color:"#666",fontSize:"10px"}}>{item.pickup_date}</div>:null}</td>
                        <td style={{ padding:"10px 12px", fontSize:"11px" }}>{item.delivery_city&&item.delivery_state?`${item.delivery_city}, ${item.delivery_state}`:''}{item.delivery_date?<div style={{color:"#666",fontSize:"10px"}}>{item.delivery_date}</div>:null}</td>
                        <td style={{ padding:"10px 12px" }}><span style={{ color:sc, fontWeight:700, fontSize:"10px", border:`1px solid ${sc}`, padding:"2px 6px" }}>{item.status||"---"}</span></td>
                        <td style={{ padding:"10px 12px", color:"#32CD32", fontSize:"11px" }}>{driver?.full_name||<span style={{color:"#ff4444"}}>UNASSIGNED</span>}</td>
                        <td style={{ padding:"10px 12px", color:"#32CD32", fontWeight:700 }}>${Number(item.rate_total||0).toLocaleString()}</td>
                        <td style={{ padding:"10px 12px", color:"#aaa" }}>{p.driver_pay_estimated>0?`$${p.driver_pay_estimated.toFixed(0)}`:"---"}</td>
                        <td style={{ padding:"10px 12px", color:"#aaa" }}>{p.fuel_estimated_cost>0?`$${p.fuel_estimated_cost.toFixed(0)}`:"---"}</td>
                        <td style={{ padding:"10px 12px", color:bc, fontWeight:700 }}>{Number(item.rate_total||0)>0?`$${p.net_profit.toFixed(0)} (${p.profit_margin_percent.toFixed(1)}%)`:"---"}</td>
                        <td style={{ padding:"10px 12px" }}><button onClick={()=>openDrillDown(item)} style={smallBtn}>OPEN</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}
    </>
  );
}
